/**
 * 通用资源查看页辅助：schema 只读配置、白噪音 exta 解析、JSON Drawer 载荷。
 * 与 createOrEdit 的加载语义对齐，避免查看页与编辑页字段不一致。
 */

import { WORKOUT_BINDING_FIELD_LABELS, WORKOUT_FIELD_LABELS, COLLECTION_COVER_EXTA_KEYS, normalizeWorkoutDifficulty } from './form/basicForms/workoutFormConstants'
import { ZONE_META, getBindingZoneEmptyText, getBindingZoneLabel } from './form/basicForms/workoutCollectionValidation'
import {
  buildZipPackageStatus,
  formatMediaDurationSeconds,
} from './form/basicForms/workoutBindingUtils'
import { fetchBindingChildSummary } from './form/basicForms/workoutBindingFetch'
import { runWithConcurrency } from './asyncConcurrency'

const L = WORKOUT_FIELD_LABELS

/** 与编辑页 fnCreateSchemaConfig 一致的 schema 布局（优惠券 / web）。 */
export function buildSchemaForAssetType(assetType) {
  const val = String(assetType || '')
  if (val.indexOf('优惠券') === -1 && val.indexOf('web(网页)') === -1) {
    return null
  }

  let schemaData = {
    brand: '',
    code: '',
    link: '',
  }
  let schemaConfig = [
    [
      {
        type: 'input',
        prop: 'brand',
        formItem: { label: 'brand' },
        attrs: { placeholder: '请输入品牌' },
        formItemProps: { trigger: 'blur' },
      },
    ],
    [
      {
        type: 'input',
        prop: 'code',
        formItem: { label: 'code' },
        attrs: { placeholder: '请输入code' },
        formItemProps: { trigger: 'blur' },
      },
    ],
    [
      {
        type: 'input',
        prop: 'link',
        formItem: { label: 'link' },
        attrs: { placeholder: '请输入link' },
        formItemProps: { trigger: 'blur' },
      },
    ],
  ]

  if (val.indexOf('web(网页)') !== -1) {
    delete schemaData.brand
    delete schemaData.code
    schemaConfig = schemaConfig.filter((item) => {
      const prop = item[0].prop
      return prop !== 'brand' && prop !== 'code'
    })
  }

  return {
    schemaData,
    schemaConfig,
    schemaRules: {},
    schemaBasicInfo: { label: '品牌基本信息' },
  }
}

/**
 * 将详情 exta 合并进 schema 初始结构，供只读 schema 表单回显。
 */
export function mergeSchemaDataFromDetail(schemaState, jsonConfig) {
  if (!schemaState || !jsonConfig) {
    return schemaState ? { ...schemaState.schemaData } : {}
  }
  const exta = jsonConfig.exta
  if (exta && typeof exta === 'object') {
    return { ...schemaState.schemaData, ...exta }
  }
  return { ...schemaState.schemaData }
}

/**
 * 白噪音：从 exta 对象解析 introduction / singnature（与编辑页 getRuleForm 一致）。
 */
export function parseWhiteNoiseTextFields(exta) {
  const result = { introduction: '', singnature: '' }
  if (!exta || typeof exta !== 'object') {
    return result
  }
  if (exta.introduction) {
    try {
      result.introduction = JSON.parse(exta.introduction)
    } catch (e) {
      result.introduction = String(exta.introduction)
    }
  }
  if (exta.singnature) {
    try {
      result.singnature = JSON.parse(exta.singnature)
    } catch (e) {
      result.singnature = String(exta.singnature)
    }
  }
  return result
}

/** 解析 jsonConfig.exta 为对象（若原为 JSON 字符串则尝试 parse）。 */
export function parseExtaFromConfig(jsonConfig) {
  const exta = jsonConfig && jsonConfig.exta
  if (exta == null || exta === '') {
    return null
  }
  if (typeof exta === 'object') {
    return exta
  }
  if (typeof exta === 'string') {
    try {
      return JSON.parse(exta)
    } catch (e) {
      return null
    }
  }
  return null
}

/** HomeWorkout 四类：是否应在查看页展示类型配置只读区。 */
export function isWorkoutAssetType(assetType) {
  return String(assetType || '').startsWith('workout_')
}

/** 与编辑页 getTypeConfigSectionTitle 对齐的查看页区块标题。 */
export function getWorkoutInfoSectionTitle(assetType) {
  const type = String(assetType || '')
  if (type === 'workout_collection') {
    return '合集编排'
  }
  if (type.startsWith('workout_')) {
    return '训练资源配置'
  }
  return '资源配置'
}

function formatBoolLabel(value) {
  if (value === true) {
    return '是'
  }
  if (value === false) {
    return '否'
  }
  return '—'
}

function formatText(value) {
  const text = value != null ? String(value).trim() : ''
  return text || '—'
}

function mapBindingRows(rows, mapper) {
  if (!Array.isArray(rows) || !rows.length) {
    return []
  }
  return rows.map((row, index) => mapper(row || {}, index))
}

function appendZipBlockFields(blockFields, assetType, jsonConfig) {
  const type = String(assetType || '')
  const zip = buildZipPackageStatus(type, jsonConfig)
  const exta = parseExtaFromConfig(jsonConfig) || {}
  let value = zip.uploaded ? `已上传 · ${zip.fileName}` : '未上传'
  let warn = !zip.uploaded
  let hint = zip.specHint || ''

  // workout_collection：url 只表示包已存 CDN；双封面才表示 zip 已解析进 exta
  if (
    type === 'workout_collection' &&
    zip.uploaded &&
    (!exta[COLLECTION_COVER_EXTA_KEYS.female] || !exta[COLLECTION_COVER_EXTA_KEYS.male])
  ) {
    warn = true
    value = `已存包 · ${zip.fileName}（未解析男女封面/config）`
    hint = [
      hint,
      '包内须含 *_cover_female.*、*_cover_male.* 与可选 *_config.json；重新上传 zip 才会触发解析（仅保存且 url 不变不会重跑）',
    ]
      .filter(Boolean)
      .join('；')
  }

  return [
    ...blockFields,
    {
      label: L.zipPackage,
      value,
      warn,
      hint,
    },
  ]
}

/**
 * 合集女/男封面 URL（无旧版 cover_image）。
 * @returns {{ female: string, male: string }}
 */
export function resolveCollectionCoverUrls(exta) {
  const source = exta && typeof exta === 'object' ? exta : {}
  return {
    female: String(source[COLLECTION_COVER_EXTA_KEYS.female] || '').trim(),
    male: String(source[COLLECTION_COVER_EXTA_KEYS.male] || '').trim(),
  }
}

/**
 * 列表 / 默认预览：优先女封面，缺则男封面。
 * @param {object} exta
 * @param {string} [fallback]
 * @returns {string}
 */
export function resolveCollectionThumbUrl(exta, fallback = '') {
  const covers = resolveCollectionCoverUrls(exta)
  return covers.female || covers.male || fallback || ''
}

function mapCollectionBindingItems(rows, zoneId, configMapper) {
  return mapBindingRows(rows, (row, index) => ({
    key: row.resource_id || `${zoneId}-${index}`,
    order: index + 1,
    title: formatText(row.resource_id),
    refLines: [],
    configLines: configMapper(row),
  }))
}

function buildCollectionBindingConfigLines(zoneId, row) {
  const B = WORKOUT_BINDING_FIELD_LABELS
  if (zoneId === 'actions') {
    const lines = [
      { key: B.videoRepeatCount, value: formatText(row.repeat_count) },
      { key: B.videoDurationSeconds, value: formatMediaDurationSeconds(row.video_duration_seconds) },
      { key: B.configuredPlaySeconds, value: formatMediaDurationSeconds(row.duration_seconds) },
    ]
    if (row.rest_seconds != null && Number(row.rest_seconds) > 0) {
      lines.push({ key: B.restSeconds, value: `${row.rest_seconds}s` })
    }
    return lines
  }
  if (zoneId === 'music') {
    return [{ key: B.loop, value: formatBoolLabel(row.loop) }]
  }
  return []
}

/**
 * 按 resource_id（key）拉子资源详情：列表只拿 id/名称，详情接口才带完整 exta。
 */
async function fetchBindingChildSummaryForView(assetType, resourceId, zoneId) {
  return fetchBindingChildSummary(assetType, resourceId, zoneId)
}

/**
 * 合集查看页：按 binding 中的 resource_id 拉子资源详情，避免依赖落库快照字段。
 */
export async function enrichWorkoutCollectionExtaView(viewModel, jsonConfig) {
  if (!viewModel || viewModel.kind !== 'collection') {
    return viewModel
  }
  const exta = parseExtaFromConfig(jsonConfig) || {}
  const bindings = await Promise.all(
    (viewModel.bindings || []).map(async (zone) => {
      const zoneMeta = ZONE_META[zone.id]
      const rows = Array.isArray(exta[zone.id]) ? exta[zone.id] : []
      const items = await runWithConcurrency(rows, async (row, index) => {
          let title = row.resource_id || '—'
          let refLines = []
          if (zoneMeta && row.resource_id) {
            try {
              const summary = await fetchBindingChildSummaryForView(
                zoneMeta.assetType,
                row.resource_id,
                zone.id
              )
              title = summary.title
              refLines = summary.refLines
            } catch (e) {
              // 子资源查询失败时仍展示 resource_id 与合集内编排参数
            }
          }
          return {
            key: row.resource_id || `${zone.id}-${index}`,
            order: index + 1,
            title: formatText(title),
            refLines,
            configLines: buildCollectionBindingConfigLines(zone.id, row),
          }
        })
      return {
        ...zone,
        label: getBindingZoneLabel(zone.id),
        emptyText: getBindingZoneEmptyText(zone.id),
        items,
      }
    })
  )
  return { ...viewModel, bindings }
}

/**
 * HomeWorkout 只读视图模型：字段集与对应 BasicForm 一致，供查看页渲染。
 */
export function buildWorkoutExtaView(assetType, jsonConfig) {
  const type = String(assetType || '')
  if (!isWorkoutAssetType(type)) {
    return null
  }
  const exta = parseExtaFromConfig(jsonConfig) || {}

  const base = {
    assetType: type,
    sectionTitle: getWorkoutInfoSectionTitle(type),
  }

  if (type === 'workout_action') {
    return {
      ...base,
      kind: 'action',
      stripVariant: 'action',
      stripFields: [
        { label: L.displayName, value: formatText(exta.display_name) },
        { label: L.difficulty, value: formatText(normalizeWorkoutDifficulty(exta.difficulty)) },
        { label: L.videoLoop, value: formatBoolLabel(exta.video_loop) },
        {
          label: L.videoDurationSeconds,
          value: (() => {
            const main = formatMediaDurationSeconds(exta.video_duration_seconds)
            const sideRaw = exta.video_side_duration_seconds
            const hasSide =
              exta.video_file_side != null && String(exta.video_file_side).trim()
            if (!hasSide) {
              return `主 ${main}  侧：—`
            }
            return `主 ${main}  侧：${formatMediaDurationSeconds(sideRaw)}`
          })(),
          warn: exta.video_duration_seconds == null || Number(exta.video_duration_seconds) <= 0,
        },
        {
          label: L.met,
          value: (() => {
            const met = exta.met != null ? exta.met : exta.calories
            return met != null ? String(met) : '—'
          })(),
          warn: (() => {
            const met = exta.met != null ? exta.met : exta.calories
            return met == null || Number(met) <= 0
          })(),
        },
      ],
      blockFields: appendZipBlockFields(
        [
          { label: L.description, value: formatText(exta.description) },
          // 可选口播：未上传时为 —（exta 无 key）；有则展示 CDN URL 供运营核对
          { label: L.switchVoiceUrl, value: formatText(exta.switch_voice_url) },
          { label: L.guideVoiceUrl, value: formatText(exta.guide_voice_url) },
        ],
        type,
        jsonConfig
      ),
    }
  }

  if (type === 'workout_music') {
    return {
      ...base,
      kind: 'music',
      stripVariant: 'music',
      stripFields: [
        { label: L.displayName, value: formatText(exta.display_name) },
        { label: L.loopable, value: formatBoolLabel(exta.loopable) },
        {
          label: L.audioDurationSeconds,
          value: formatMediaDurationSeconds(exta.audio_duration_seconds),
          warn: exta.audio_duration_seconds == null || Number(exta.audio_duration_seconds) <= 0,
        },
      ],
      blockFields: appendZipBlockFields(
        [{ label: L.description, value: formatText(exta.description) }],
        type,
        jsonConfig
      ),
    }
  }

  if (type === 'workout_voice') {
    return {
      ...base,
      kind: 'voice',
      stripVariant: 'voice',
      stripFields: [
        { label: L.displayName, value: formatText(exta.display_name) },
        { label: L.gender, value: formatText(exta.gender) },
        { label: L.language, value: formatText(exta.language) },
      ],
      blockFields: appendZipBlockFields(
        [{ label: L.description, value: formatText(exta.description) }],
        type,
        jsonConfig
      ),
    }
  }

  if (type === 'workout_collection') {
    return {
      ...base,
      kind: 'collection',
      stripVariant: 'collection',
      stripFieldRows: [
        [
          { label: L.displayName, value: formatText(exta.display_name) },
          { label: L.subtitle, value: formatText(exta.subtitle) },
          { label: L.difficulty, value: formatText(normalizeWorkoutDifficulty(exta.difficulty)) },
        ],
        [
          { label: L.description, value: formatText(exta.description) },
          {
            label: L.totalDurationSeconds,
            value: formatMediaDurationSeconds(exta.total_duration_seconds),
            warn: exta.total_duration_seconds == null || Number(exta.total_duration_seconds) <= 0,
          },
        ],
      ],
      blockFields: appendZipBlockFields([], type, jsonConfig),
      bindings: [
        {
          id: 'actions',
          label: getBindingZoneLabel('actions'),
          emptyText: getBindingZoneEmptyText('actions'),
          items: mapCollectionBindingItems(exta.actions, 'actions', (row) =>
            buildCollectionBindingConfigLines('actions', row)
          ),
        },
        {
          id: 'music',
          label: getBindingZoneLabel('music'),
          emptyText: getBindingZoneEmptyText('music'),
          items: mapCollectionBindingItems(exta.music, 'music', (row) =>
            buildCollectionBindingConfigLines('music', row)
          ),
        },
        {
          id: 'voices',
          label: getBindingZoneLabel('voices'),
          emptyText: getBindingZoneEmptyText('voices'),
          items: mapCollectionBindingItems(exta.voices, 'voices', (row) =>
            buildCollectionBindingConfigLines('voices', row)
          ),
        },
      ],
    }
  }

  return null
}

/** 是否应按 recipe / 结构化资源展示 exta（避免主内容区 dump 整段 JSON）。 */
export function isStructuredExta(extaObj) {
  if (!extaObj || typeof extaObj !== 'object') {
    return false
  }
  return Boolean(extaObj.recipe || extaObj.previewUrl || extaObj.jsonUrl)
}

/**
 * 结构化 exta 只读视图数据：recipe 营养、步骤等。
 * 完整 JSON 仍通过顶栏 JSON 面板查看。
 */
export function buildStructuredExtaView(extaObj) {
  if (!extaObj || typeof extaObj !== 'object') {
    return null
  }
  const recipe = extaObj.recipe || {}
  const nutrition = recipe.nutrition || {}
  const directions = Array.isArray(recipe.directions) ? recipe.directions : []

  return {
    previewUrl: extaObj.previewUrl || '',
    jsonUrl: extaObj.jsonUrl || '',
    cookTime: recipe.cook_time || recipe.cookTime || '',
    nutrition: {
      calories: nutrition.calories,
      protein: nutrition.protein,
      carbs: nutrition.carbs,
      fat: nutrition.fat,
    },
    directions: directions.map((step, index) => ({
      index: index + 1,
      text: typeof step === 'string' ? step : (step && step.text) || String(step),
    })),
  }
}

/** 普通字符串 exta 只读展示（非对象时）。 */
export function formatPlainExtaText(exta) {
  if (exta == null || exta === '') {
    return '—'
  }
  if (typeof exta === 'object') {
    return '—'
  }
  return String(exta)
}

/** @deprecated 主内容区不再 dump 对象 JSON，保留供兼容引用 */
export function formatExtaDisplay(exta) {
  return formatPlainExtaText(exta)
}

/** 投放 App 版本列表：skip 数组转逗号字符串，与编辑页 getAppVersion 一致。 */
export function normalizeAppVersionListForView(list) {
  if (!list || !list.length) {
    return [
      {
        start: '',
        end: '',
        skip: '',
        dataSetId: '',
      },
    ]
  }
  return list.map((element) => ({
    ...element,
    skip: Array.isArray(element.skip) ? element.skip.join(',') : element.skip || '',
  }))
}

/**
 * 基本信息「配置数据集」汇总：合并解锁覆盖与应用版本过滤中的数据集名，去重保序。
 * 不含列表 dataSetId 筛选所用的「分类归属数据集」；后者待详情接口补充后再展示。
 */
export function collectResourceDatasetNames(lockGroupList, appVersionList) {
  const names = []
  const seen = new Set()

  const pushName = (raw) => {
    const name = raw != null ? String(raw).trim() : ''
    if (!name || seen.has(name)) {
      return
    }
    seen.add(name)
    names.push(name)
  }

  ;(lockGroupList || []).forEach((item) => {
    if (item) {
      pushName(item.dataSetName)
    }
  })
  ;(appVersionList || []).forEach((item) => {
    if (item) {
      pushName(item.dataSetName)
    }
  })

  return names
}

/** 数据集解锁覆盖只读行：解锁组 ↔ 数据集，与编辑页 dataSetLockGroupList 同源。 */
export function buildLockOverrideRows(lockGroupList) {
  if (!lockGroupList || !lockGroupList.length) {
    return []
  }
  return lockGroupList
    .filter((item) => {
      if (!item) {
        return false
      }
      return Boolean(
        item.lockGroupName ||
          item.lockGroupId ||
          item.dataSetName ||
          item.dataSetId
      )
    })
    .map((item) => ({
      lockGroup:
        item.lockGroupName || (item.lockGroupId != null ? String(item.lockGroupId) : '—'),
      dataSet: item.dataSetName || (item.dataSetId != null ? String(item.dataSetId) : '—'),
    }))
}

/** 国家过滤只读文案。 */
export function buildCountryFilterView(countryList) {
  const countries = countryList && countryList.countries
  if (!countries || !countries.length) {
    return { configured: false, text: '未配置' }
  }
  const mode = countryList.isExclude ? '不包含' : '包含'
  return {
    configured: true,
    text: `${mode}：${countries.join('、')}`,
  }
}

/** 系统版本区间只读文案。 */
export function buildOsVersionFilterText(begin, over, neglect) {
  const hasBegin = begin !== '' && begin != null && begin !== undefined
  const hasOver = over !== '' && over != null && over !== undefined
  const neglectStr = neglect ? String(neglect).trim() : ''
  if (!hasBegin && !hasOver && !neglectStr) {
    return '未配置'
  }
  const parts = []
  if (hasBegin || hasOver) {
    parts.push(`${hasBegin ? begin : '—'} ~ ${hasOver ? over : '—'}`)
  }
  if (neglectStr) {
    parts.push(`跳过：${neglectStr}`)
  }
  return parts.join('；')
}

/** 应用版本规则只读行。 */
export function buildAppVersionFilterRows(list) {
  if (!list || !list.length) {
    return []
  }
  return list
    .filter((item) => {
      if (!item) {
        return false
      }
      return Boolean(
        item.start ||
          item.end ||
          item.skip ||
          item.dataSetId ||
          item.dataSetName
      )
    })
    .map((item) => ({
      range: `${item.start !== '' && item.start != null ? item.start : '—'} ~ ${
        item.end !== '' && item.end != null ? item.end : '—'
      }`,
      skip: item.skip ? String(item.skip) : '—',
      dataSet: item.dataSetName || (item.dataSetId ? String(item.dataSetId) : '—'),
    }))
}

/** 是否存在任意已配置的过滤项（用于空态）。 */
export function hasFilterConfiguration(info) {
  if (!info) {
    return false
  }
  if (buildCountryFilterView(info.countryList).configured) {
    return true
  }
  if (buildOsVersionFilterText(
    info.androidBeginVersion,
    info.androidOverVersion,
    info.androidNeglectVersion
  ) !== '未配置') {
    return true
  }
  if (buildOsVersionFilterText(
    info.iosBeginVersion,
    info.iosOverVersion,
    info.iosNeglectVersion
  ) !== '未配置') {
    return true
  }
  return buildAppVersionFilterRows(info.wallpaperAppVersionList).length > 0
}

/** 从详情 VO 读取 jsonConfig.url（zip 包 CDN 地址）。 */
export function resolveResourceZipUrl(info) {
  if (!info || typeof info !== 'object') {
    return ''
  }
  const jsonConfig = info.jsonConfig
  const url = jsonConfig && jsonConfig.url != null ? String(jsonConfig.url).trim() : ''
  return url
}

/** 下载文件名：优先 url 末段，否则 key + assetType.zip。 */
export function resolveResourceZipFileName(info, url = resolveResourceZipUrl(info)) {
  if (url) {
    const segment = url.split('/').pop()
    if (segment) {
      return decodeURIComponent(segment.split('?')[0])
    }
  }
  const key = info && info.key ? String(info.key).trim() : 'resource'
  const type = info && info.assetType ? String(info.assetType).trim() : 'file'
  return `${key}-${type}.zip`
}

/**
 * 查看页「下载资源」：向后端申请下载 URL（Spaces 预签名或历史公开 CDN 直链）再触发浏览器下载。
 * @returns {Promise<{ ok: boolean, message?: string }>}
 */
export async function downloadResourceZipFile(info, fetchDownloadCredential) {
  const url = resolveResourceZipUrl(info)
  if (!url) {
    return { ok: false, message: '该资源尚未上传 zip 包' }
  }
  const id = info && info.id != null ? info.id : null
  if (!id) {
    return { ok: false, message: '缺少资源 id，无法获取下载链接' }
  }
  const requestCredential =
    fetchDownloadCredential ||
    ((resourceId) =>
      import('../../../api/genericResource').then(({ getGenericResourceDownloadZip }) =>
        getGenericResourceDownloadZip({ id: resourceId })
      ))
  try {
    const res = await requestCredential(id)
    if (!res || !res.success || !res.data || !res.data.downloadUrl) {
      return {
        ok: false,
        message: (res && (res.errMessage || res.message)) || '获取下载链接失败',
      }
    }
    triggerBrowserFileDownload(
      res.data.downloadUrl,
      res.data.fileName || resolveResourceZipFileName(info, url)
    )
    return { ok: true }
  } catch (error) {
    return {
      ok: false,
      // axios 业务拒绝体是 { errMessage }，不是 Error.message
      message: (error && (error.errMessage || error.message)) || '获取下载链接失败',
    }
  }
}

function triggerBrowserFileDownload(url, fileName) {
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * 查看页「原始 JSON」载荷：仅展示 jsonConfig（与运营编辑/落库字段对齐）。
 */
export function buildJsonConfigViewPayload(info) {
  if (!info || typeof info !== 'object') {
    return {}
  }
  const jsonConfig = info.jsonConfig
  if (!jsonConfig || typeof jsonConfig !== 'object') {
    return {}
  }
  return jsonConfig
}

/** @deprecated 查看页已改用 buildJsonConfigViewPayload；保留供排障脚本引用 */
export function buildJsonDrawerPayload(info) {
  return buildJsonConfigViewPayload(info)
}
